import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';

/**
 * 認証ブートストラップの失敗表示。
 *
 * セッション切れ（401 → ログインへ誘導）とは別に、現在ユーザーの取得そのものが失敗した
 * ケースを明示する。app.tsx から切り出しているのは、ルート定義とレイアウトが1ファイルに
 * 混ざると見通しが落ちるため。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Tells the user that signing in failed for a reason other than an expired session, instead of leaving a blank screen.
 * @graph-connects none
 */

/** @graph-connects none */
interface AuthErrorStateProps {
  error: unknown;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business セッション切れ以外の理由で現在ユーザー取得が失敗した場合に、明示的な認証ブートストラップ失敗状態を表示する。
 * @graph-connects none
 */
export function AuthErrorState({ error }: AuthErrorStateProps) {
  const { t } = useTranslation();
  const message = error instanceof Error ? error.message : t('auth.errorFallback');

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[var(--color-bg-base)] p-6 text-[var(--color-text-primary)]">
      <div
        className="gradient-orb"
        style={{
          width: '400px',
          height: '400px',
          top: '20%',
          left: '30%',
          background: 'var(--color-error)',
        }}
      />

      <Card className="animate-fade-in-up relative w-full max-w-lg border-[var(--color-error)]/20 p-8">
        <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-error)] uppercase">
          Authentication Error
        </div>
        <h1 className="mt-3 text-2xl font-bold">{t('auth.errorTitle')}</h1>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          {t('auth.errorDescription')}
        </p>
        <div className="mt-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-base)] px-4 py-3 font-mono text-sm text-[var(--color-text-secondary)]">
          {message}
        </div>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('auth.reload')}
        </Button>
      </Card>
    </div>
  );
}
